import { useEffect, useRef, useState } from "react";

// Enforces single-tab usage via a localStorage-based lock with a heartbeat.
// Whichever tab claims the lock first stays "active" and keeps refreshing
// its heartbeat. Any other tab sees the lock is held (and recent) and
// blocks itself. If the active tab closes normally, it releases the lock
// immediately (beforeunload) and the blocked tab takes over on the very
// next `storage` event — no manual refresh needed. If the active tab
// crashes without releasing the lock, the heartbeat goes stale after
// STALE_MS and the blocked tab reclaims it on its next poll.
const LOCK_KEY = "codebook_app_active_tab";
const HEARTBEAT_MS = 2000;
const STALE_MS = 5000;

const generateTabId = () => `${Date.now()}-${Math.random().toString(36).slice(2)}`;

export const useSingleTabEnforcement = () => {
  const tabIdRef = useRef(generateTabId());
  const [isBlocked, setIsBlocked] = useState(false);

  useEffect(() => {
    const readLock = () => {
      try {
        const raw = localStorage.getItem(LOCK_KEY);
        return raw ? JSON.parse(raw) : null;
      } catch {
        return null;
      }
    };

    const writeLock = () => {
      localStorage.setItem(
        LOCK_KEY,
        JSON.stringify({ tabId: tabIdRef.current, ts: Date.now() })
      );
    };

    const clearLockIfOwn = () => {
      const lock = readLock();
      if (lock?.tabId === tabIdRef.current) {
        localStorage.removeItem(LOCK_KEY);
      }
    };

    // Claims the lock if it's free/stale/already ours; otherwise blocks.
    const tryBecomeActive = () => {
      const lock = readLock();
      const isFree = !lock || Date.now() - lock.ts > STALE_MS;
      const isOwn = lock?.tabId === tabIdRef.current;

      if (isFree || isOwn) {
        writeLock();
        setIsBlocked(false);
      } else {
        setIsBlocked(true);
      }
    };

    tryBecomeActive();

    // Heartbeat + periodic recheck in one interval: refreshes our own
    // lock if active, or notices if the lock has gone stale if blocked.
    const interval = setInterval(tryBecomeActive, HEARTBEAT_MS);

    // Fires in THIS tab whenever another tab writes/removes the key —
    // lets a blocked tab react the instant the active tab releases it.
    const onStorage = (e) => {
      if (e.key === LOCK_KEY) tryBecomeActive();
    };
    window.addEventListener("storage", onStorage);

    const onBeforeUnload = () => clearLockIfOwn();
    window.addEventListener("beforeunload", onBeforeUnload);

    return () => {
      clearInterval(interval);
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("beforeunload", onBeforeUnload);
      clearLockIfOwn();
    };
  }, []);

  return { isBlocked };
};